import { useCallback, useEffect, useRef, useState } from "react";
import { useToastContext } from "../../../../../contexts/ToastContext/ToastContext";
import { redirectIfUnauthorized } from "../../../utils/adminUtils";
import { AdminApiError, getErrorCode } from "../../../hooks/useAdminList";
import { toApiDate } from "../reportUtils";

export const PERIODS = {
  ALL: "all",
  THIS_MONTH: "thisMonth",
  LAST_MONTH: "lastMonth",
  THIS_YEAR: "thisYear",
  CUSTOM: "custom",
};

// "Tudo" começa em 2000-01-01, igual ao padrão dos relatórios clássicos
export const periodRange = (period, customRange) => {
  const today = new Date();
  switch (period) {
    case PERIODS.THIS_MONTH:
      return [new Date(today.getFullYear(), today.getMonth(), 1), today];
    case PERIODS.LAST_MONTH:
      return [
        new Date(today.getFullYear(), today.getMonth() - 1, 1),
        new Date(today.getFullYear(), today.getMonth(), 0),
      ];
    case PERIODS.THIS_YEAR:
      return [new Date(today.getFullYear(), 0, 1), today];
    case PERIODS.CUSTOM:
      return customRange;
    default:
      return [new Date(2000, 0, 1), today];
  }
};

// Carrega o relatório do período escolhido e exporta o PDF do mesmo período
const useReport = ({ load, loadPdf, pdfFileName }) => {
  const { showErrorToast, showSuccessToast } = useToastContext();
  const [period, setPeriod] = useState(PERIODS.ALL);
  const [customRange, setCustomRange] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const requestRef = useRef(0);

  const [start, end] = periodRange(period, customRange) || [];
  const dateBegin = start ? toApiDate(start) : null;
  const dateEnd = end ? toApiDate(end) : null;

  const fetchRows = useCallback(async () => {
    if (!dateBegin || !dateEnd) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    try {
      const result = await load(dateBegin, dateEnd);
      if (requestId !== requestRef.current) return;
      setRows(result);
      setLoadError(false);
    } catch (e) {
      if (requestId !== requestRef.current || redirectIfUnauthorized(e)) return;
      console.log(e);
      setRows([]);
      setLoadError(true);
      if (e instanceof AdminApiError) showErrorToast({ code: getErrorCode(e) });
      else showErrorToast({ detail: "errorFetchingData" });
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateBegin, dateEnd]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const changePeriod = (nextPeriod, nextRange = null) => {
    setPeriod(nextPeriod);
    setCustomRange(nextRange);
  };

  const downloadPdf = async () => {
    if (!dateBegin || !dateEnd) return;
    setDownloading(true);
    try {
      const response = await loadPdf(dateBegin, dateEnd);
      if (!response?.data) return;
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${pdfFileName}-${dateBegin}_${dateEnd}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showSuccessToast({ detail: "pdfDownloadedSuccessfully" });
    } catch (e) {
      if (redirectIfUnauthorized(e)) return;
      console.log(e);
      showErrorToast({ detail: "errorDownloadingPdf" });
    } finally {
      setDownloading(false);
    }
  };

  return {
    rows,
    loading,
    loadError,
    reload: fetchRows,
    period,
    customRange,
    range: [start, end],
    changePeriod,
    downloadPdf,
    downloading,
  };
};

export default useReport;
